// apps/web/src/features/commercial-proposals/public/publicApi.ts
//
// @aiPurpose dedicated NO-AUTH client for the unauthenticated public
//   commercial-proposal surface (#1199). It deliberately does NOT route
//   through `api/client.ts`: that client attaches the bearer access token
//   and runs the 401 → /auth/refresh retry, neither of which may touch a
//   public page (a logged-out customer has no session, and any refresh
//   attempt would leak auth state onto a public surface). Plain `fetch`
//   with `credentials: 'omit'` keeps the request anonymous.
// @aiSecurity the bearer token travels in the JSON BODY, never the URL, so
//   it never lands in access logs or a `Referer` header. Every non-2xx and
//   every validation failure collapses into the SAME opaque
//   `PublicProposalNotFoundError` so the page cannot distinguish
//   unknown / malformed / draft / revoked — mirroring the backend's uniform
//   404 (no existence leak).
import {
  CommercialProposalPublicDtoSchema,
  type CommercialProposalPublicDto,
} from '@magimp/shared';

const PUBLIC_BASE = '/api/public/commercial-proposals';

/**
 * @aiPurpose the single opaque failure the public page may observe. It
 *   carries NO reason detail by design — the page must render one generic
 *   not-found regardless of why resolution failed.
 */
export class PublicProposalNotFoundError extends Error {
  constructor() {
    super('public-proposal-not-found');
    this.name = 'PublicProposalNotFoundError';
  }
}

function postToken(action: 'resolve' | 'pdf' | 'photo', token: string): Promise<Response> {
  return fetch(`${PUBLIC_BASE}/${action}`, {
    method: 'POST',
    // INVARIANT (#1199): no Authorization header and no cookies — the public
    // surface is strictly anonymous; the token is the only credential and it
    // rides in the body below.
    credentials: 'omit',
    cache: 'no-store',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

/**
 * @throws {PublicProposalNotFoundError} for any non-2xx response or any
 *   shape that fails `CommercialProposalPublicDtoSchema` — never the raw
 *   error, so a draft/revoked/unknown token is indistinguishable.
 */
export async function resolvePublicProposal(token: string): Promise<CommercialProposalPublicDto> {
  const res = await postToken('resolve', token);
  if (!res.ok) throw new PublicProposalNotFoundError();
  const parsed = CommercialProposalPublicDtoSchema.safeParse(await res.json().catch(() => null));
  if (!parsed.success) throw new PublicProposalNotFoundError();
  return parsed.data;
}

/** @throws {PublicProposalNotFoundError} on any non-2xx response. */
export async function downloadPublicProposalPdf(token: string): Promise<Blob> {
  const res = await postToken('pdf', token);
  if (!res.ok) throw new PublicProposalNotFoundError();
  return res.blob();
}

/** @throws {PublicProposalNotFoundError} on any non-2xx response. */
export async function fetchPublicProposalPhoto(token: string): Promise<Blob> {
  const res = await postToken('photo', token);
  if (!res.ok) throw new PublicProposalNotFoundError();
  return res.blob();
}

/**
 * @aiPurpose extract the public token from a URL hash fragment of the form
 *   `#t=<token>` (the backend builds the link path as `/p#t=<token>`). The
 *   token is in the FRAGMENT so it never reaches the server in a query
 *   string. Returns null when absent or empty.
 */
export function parsePublicTokenFromHash(hash: string): string | null {
  const rest = hash.startsWith('#') ? hash.slice(1) : hash;
  const token = new URLSearchParams(rest).get('t');
  return token ? token : null;
}
